import { useEffect, useRef } from 'react';

import { match } from 'ts-pattern';

import { Permissions } from '@cloud-wave/neon-common-lib';

import { useConfigContext } from 'lib/core/config';
import { useAuthContext } from 'lib/core/context/AuthProvider';

import { LogEvents, logger } from 'lib/common/components/LoggerController';

import { useAgentPreferencesContext } from '../AgentPreferenceContext';
import { useLogStoreContext } from '../LogStoreContext';
import { usePermissionsContext } from '../PermissionsContext';
import { usePreferencesContext } from '../PreferencesContext';
import { LogStreamFilter } from '../PreferencesContext/Context';
import Context from './Context';
import createLogStream from './api/createLogStream';
import {
  resetEnhancedLoggingPreferenceToOriginal,
  storeOriginalEnhancedLoggingPreference,
} from './utils/storeAndResetOriginalEnhancedLoggingPreference';
import uploadLogsWithStreamRecovery from './utils/uploadLogsWithStreamRecovery';

const LOG_STREAMING_INTERVAL = 10000; // 10 seconds
const LOG_EVENTS_LIMIT = 10_000; // Limit to 10,000 log events per stream

export const LogStreamProvider = ({ children }) => {
  const { fetch_ } = useAuthContext();
  const { config } = useConfigContext();

  const {
    actions: { setEnhancedLoggingEnabled, setLogStreamFilter },
    state: { enhancedLoggingEnabled, logStreamFilter },
  } = usePreferencesContext();
  const { hasPermission } = usePermissionsContext();
  const {
    state: { agentPreference },
  } = useAgentPreferencesContext();
  const {
    actions: { getLogsToSend, markLogsAsSentAndPrune },
  } = useLogStoreContext();

  const lastSentAtTimestamp = useRef<number>(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const isStreamingLogs = useRef(false);

  const logStreamingEnabled = Boolean(
    hasPermission({
      type: 'tenant',
      permission: Permissions.ENHANCED_LOGGING_ALL_AGENTS,
    }) || agentPreference?.centralisedLogging
  );

  // https://cloud-wave.atlassian.net/wiki/spaces/PROD/pages/2147385354/How+it+works#Streaming-CC-and-NN-logs
  useEffect(() => {
    if (!logStreamingEnabled) {
      // If we've previously changed enhanced logging, reset it to the user's original preference
      resetEnhancedLoggingPreferenceToOriginal(setEnhancedLoggingEnabled);
      return;
    }

    // Store the user's preference for enhanced logging before changing it for log streaming
    storeOriginalEnhancedLoggingPreference(enhancedLoggingEnabled);

    if (
      hasPermission({
        type: 'tenant',
        permission: Permissions.ENHANCED_LOGGING_CONNECT_LOGS_ONLY,
      })
    ) {
      setLogStreamFilter(LogStreamFilter.CONNECT);
      setEnhancedLoggingEnabled(false);
      return;
    }

    if (
      hasPermission({
        type: 'tenant',
        permission: Permissions.ENHANCED_LOGGING_NEONNOW_LOGS_ONLY,
      })
    ) {
      setLogStreamFilter(LogStreamFilter.NEONNOW);
      setEnhancedLoggingEnabled(true);
      return;
    }

    setLogStreamFilter(LogStreamFilter.ALL);
    setEnhancedLoggingEnabled(true);
  }, [hasPermission, logStreamingEnabled]);

  async function streamNeonNowLogs() {
    if (isStreamingLogs.current) {
      return;
    }
    isStreamingLogs.current = true;

    const logEntries = await getLogsToSend();

    if (!logEntries.length) {
      isStreamingLogs.current = false;

      // No more logs to send
      return 0;
    }

    try {
      await uploadLogsWithStreamRecovery({ fetch_, config, logs: logEntries });

      await markLogsAsSentAndPrune(logEntries[logEntries.length - 1].timestamp);
    } catch (error) {
      logger.error(LogEvents.LOGGING.LOG_UPLOAD_FAILED, { error });
    } finally {
      isStreamingLogs.current = false;
    }
  }

  async function streamConnectLogs() {
    if (isStreamingLogs.current) {
      return;
    }
    isStreamingLogs.current = true;

    const rawLogs = connect.getLog()['_logs'];

    const parsedLogs = rawLogs.map((log, index) => ({
      message: JSON.stringify(log),
      timestamp: new Date(log.time).valueOf(),
      originalIndex: index,
    }));

    const unsentLogs = parsedLogs.filter((log) => log.timestamp > lastSentAtTimestamp.current);

    if (unsentLogs.length === 0) {
      isStreamingLogs.current = false;
      return;
    }

    unsentLogs.sort((a, b) => a.timestamp - b.timestamp || a.originalIndex - b.originalIndex);

    const logsToSend = unsentLogs.slice(0, LOG_EVENTS_LIMIT).map((log) => ({
      message: log.message,
      timestamp: log.timestamp,
    }));

    if (!logsToSend.length) {
      isStreamingLogs.current = false;

      return;
    }

    try {
      await uploadLogsWithStreamRecovery({ fetch_, config, logs: logsToSend });

      lastSentAtTimestamp.current = logsToSend[logsToSend.length - 1].timestamp + 1;
    } catch (error) {
      logger.error(LogEvents.LOGGING.LOG_UPLOAD_FAILED, { error });
    } finally {
      isStreamingLogs.current = false;
    }
  }

  async function startLogStreaming() {
    try {
      await createLogStream({ fetch_, config });

      const intervalFunction = await match(logStreamFilter)
        .with(LogStreamFilter.NEONNOW, () => streamNeonNowLogs)
        .with(LogStreamFilter.CONNECT, () => streamConnectLogs)
        .with(LogStreamFilter.ALL, async () => {
          // Stream all older NEONNOW logs, until exhausted. Then stream CONNECT logs on interval.
          while (true) {
            const remainingLogs = await streamNeonNowLogs();
            if (remainingLogs === 0) {
              break;
            }
          }

          return streamConnectLogs;
        })
        .otherwise(() => undefined);

      if (intervalFunction) {
        intervalRef.current = setInterval(intervalFunction, LOG_STREAMING_INTERVAL);
      }
    } catch (error) {
      logger.error(LogEvents.LOGGING.LOG_STREAM_CREATION_FAILED, { error });
    }
  }

  useEffect(() => {
    if (Boolean(logStreamFilter) && logStreamingEnabled) {
      startLogStreaming();
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [logStreamingEnabled, logStreamFilter]);

  return (
    <Context.Provider
      value={{
        state: {
          logStreamingEnabled,
        },
      }}
    >
      {children}
    </Context.Provider>
  );
};

export default LogStreamProvider;
